import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, Eye, Users, DollarSign, Package,
  FileText, Building, Calendar, ArrowUp, ArrowDown, Minus,
  Home, Car, Truck, Sofa, Filter, RefreshCw, Download,
  MessageSquare, Heart, Share2, ExternalLink, Loader2
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { api } from '../lib/auth';

// Stats Card Component
function StatCard({ title, value, change, changeType, icon: Icon, color }) {
  return (
    <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {change !== undefined && (
          <div className={`flex items-center text-sm ${
            changeType === 'up' ? 'text-emerald-400' : changeType === 'down' ? 'text-red-400' : 'text-gray-400'
          }`}>
            {changeType === 'up' ? <ArrowUp className="w-4 h-4" /> : 
             changeType === 'down' ? <ArrowDown className="w-4 h-4" /> : 
             <Minus className="w-4 h-4" />}
            <span className="ml-1">{change}%</span>
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-gray-400 mt-1">{title}</p>
    </div>
  );
}

// Mini Bar Chart Component
function MiniBarChart({ data, color }) {
  const max = Math.max(...data.map(d => d.value));
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center">
          <div 
            className={`w-full bg-gradient-to-t ${color} rounded-t`}
            style={{ height: `${(item.value / max) * 100}%`, minHeight: '4px' }}
          />
          <span className="text-[10px] text-gray-500 mt-1">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// Analytics Section Component
function AnalyticsSection({ title, description, icon: Icon, color, children }) {
  return (
    <div className="bg-[#0a0f1a] rounded-2xl border border-white/10 overflow-hidden">
      <div className={`px-6 py-4 bg-gradient-to-r ${color} border-b border-white/10`}>
        <div className="flex items-center gap-3">
          <Icon className="w-6 h-6 text-white" />
          <div>
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            <p className="text-sm text-white/70">{description}</p>
          </div>
        </div>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
}

export default function AnalyticsDashboardPage() {
  const [activeTab, setActiveTab] = useState('inventory');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d');
  const [inventoryStats, setInventoryStats] = useState(null);
  const [contentStats, setContentStats] = useState(null);
  const [directoryStats, setDirectoryStats] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Load inventory stats
      const [reRes, vehRes, rvRes, furnRes] = await Promise.all([
        api.get('/api/directory/inventory/real-estate').catch(() => ({ data: { listings: [], total: 0 } })),
        api.get('/api/directory/inventory/vehicles').catch(() => ({ data: { listings: [], total: 0 } })),
        api.get('/api/directory/inventory/rvs').catch(() => ({ data: { listings: [], total: 0 } })),
        api.get('/api/directory/inventory/furniture').catch(() => ({ data: { listings: [], total: 0 } }))
      ]);

      const inventoryListings = {
        realEstate: reRes.data.listings || [],
        vehicles: vehRes.data.listings || [],
        rvs: rvRes.data.listings || [],
        furniture: furnRes.data.listings || []
      };

      const totalListings = inventoryListings.realEstate.length + inventoryListings.vehicles.length + 
                           inventoryListings.rvs.length + inventoryListings.furniture.length;
      const publishedListings = [...inventoryListings.realEstate, ...inventoryListings.vehicles, 
                                 ...inventoryListings.rvs, ...inventoryListings.furniture]
                                 .filter(l => l.publish_status === 'published').length;
      const totalViews = [...inventoryListings.realEstate, ...inventoryListings.vehicles,
                          ...inventoryListings.rvs, ...inventoryListings.furniture]
                          .reduce((sum, l) => sum + (l.views || 0), 0);
      const totalInquiries = [...inventoryListings.realEstate, ...inventoryListings.vehicles,
                              ...inventoryListings.rvs, ...inventoryListings.furniture]
                              .reduce((sum, l) => sum + (l.inquiries || 0), 0);

      setInventoryStats({
        total: totalListings,
        published: publishedListings,
        draft: totalListings - publishedListings,
        views: totalViews,
        inquiries: totalInquiries,
        byType: [
          { label: 'RE', value: inventoryListings.realEstate.length, name: 'Real Estate' },
          { label: 'Veh', value: inventoryListings.vehicles.length, name: 'Vehicles' },
          { label: 'RV', value: inventoryListings.rvs.length, name: 'RVs' },
          { label: 'Furn', value: inventoryListings.furniture.length, name: 'Furniture' }
        ]
      });

      // Load content stats
      const contentRes = await api.get('/api/content/my-content').catch(() => ({ data: [] }));
      const contentList = contentRes.data || [];
      const publishedContent = contentList.filter(c => c.published).length;
      const contentViews = contentList.reduce((sum, c) => sum + (c.views || 0), 0);

      setContentStats({
        total: contentList.length,
        published: publishedContent,
        draft: contentList.length - publishedContent,
        views: contentViews,
        engagement: contentList.reduce((sum, c) => sum + (c.likes || 0) + (c.comments || 0), 0),
        byType: contentList.reduce((acc, c) => {
          const type = c.content_type || 'other';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {})
      });

      // Load directory stats
      const businessRes = await api.getMyBusinessListing().catch(() => ({ data: null }));
      const business = businessRes.data;

      setDirectoryStats({
        hasListing: !!business,
        isPublished: business?.publish_status === 'published',
        views: business?.views || 0,
        likes: business?.like_count || 0,
        inquiries: business?.inquiries || 0,
        rating: business?.rating || 0,
        reviewCount: business?.review_count || 0
      });

    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'inventory', label: 'Inventory Analytics', icon: Package },
    { id: 'content', label: 'Publishing Content', icon: FileText },
    { id: 'directory', label: 'Directory Listing', icon: Building }
  ];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Analytics Dashboard</h1>
              <p className="text-gray-400 mt-1">Track performance across inventory, content, and directory</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="bg-[#0d1525] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                style={{ backgroundColor: '#0d1525' }}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
            <Button variant="outline" onClick={loadAnalytics} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-4">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
          </div>
        ) : (
          <>
            {/* Inventory Analytics */}
            {activeTab === 'inventory' && inventoryStats && (
              <AnalyticsSection
                title="Inventory Analytics"
                description="Performance metrics for Real Estate, Vehicles, RVs, and Furniture listings"
                icon={Package}
                color="from-violet-600/80 to-purple-600/80"
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard
                    title="Total Listings"
                    value={inventoryStats.total}
                    icon={Package}
                    color="from-violet-500 to-purple-600"
                  />
                  <StatCard
                    title="Published"
                    value={inventoryStats.published}
                    change={inventoryStats.total > 0 ? Math.round((inventoryStats.published / inventoryStats.total) * 100) : 0}
                    changeType={inventoryStats.published > 0 ? 'up' : 'neutral'}
                    icon={Eye}
                    color="from-emerald-500 to-teal-600"
                  />
                  <StatCard
                    title="Total Views"
                    value={inventoryStats.views.toLocaleString()}
                    icon={TrendingUp}
                    color="from-blue-500 to-cyan-600"
                  />
                  <StatCard
                    title="Inquiries"
                    value={inventoryStats.inquiries}
                    icon={MessageSquare}
                    color="from-amber-500 to-orange-600"
                  />
                </div>

                {/* Breakdown by Type */}
                <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
                  <h3 className="font-semibold text-white mb-4">Listings by Type</h3>
                  <div className="grid grid-cols-4 gap-4">
                    {inventoryStats.byType.map(type => (
                      <div key={type.label} className="text-center">
                        <div className="text-2xl font-bold text-white">{type.value}</div>
                        <div className="text-sm text-gray-400">{type.name}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6">
                    <MiniBarChart data={inventoryStats.byType} color="from-violet-500 to-purple-600" />
                  </div>
                </div>

                {/* Top Performing */}
                <div className="mt-6 p-4 bg-cyan-900/20 border border-cyan-500/30 rounded-lg">
                  <p className="text-sm text-cyan-300">
                    <strong>💡 Tip:</strong> Publish more listings to increase visibility. Published listings appear in the AI Business Finder directory and can be discovered by potential customers.
                  </p>
                </div>
              </AnalyticsSection>
            )}

            {/* Publishing Content Analytics */}
            {activeTab === 'content' && contentStats && (
              <AnalyticsSection
                title="Publishing Content Analytics"
                description="Performance metrics for articles, ads, promotions, and media content"
                icon={FileText}
                color="from-blue-600/80 to-cyan-600/80"
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard
                    title="Total Content"
                    value={contentStats.total}
                    icon={FileText}
                    color="from-blue-500 to-cyan-600"
                  />
                  <StatCard
                    title="Published"
                    value={contentStats.published}
                    icon={Eye}
                    color="from-emerald-500 to-teal-600"
                  />
                  <StatCard
                    title="Total Views"
                    value={contentStats.views.toLocaleString()}
                    icon={TrendingUp}
                    color="from-violet-500 to-purple-600"
                  />
                  <StatCard
                    title="Engagement"
                    value={contentStats.engagement}
                    icon={Heart}
                    color="from-pink-500 to-rose-600"
                  />
                </div>

                {/* Content by Type */}
                <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
                  <h3 className="font-semibold text-white mb-4">Content by Type</h3>
                  {Object.keys(contentStats.byType).length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(contentStats.byType).map(([type, count]) => (
                        <div key={type} className="text-center p-3 bg-white/5 rounded-lg">
                          <div className="text-xl font-bold text-white">{count}</div>
                          <div className="text-xs text-gray-400 capitalize">{type.replace(/_/g, ' ')}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center py-4">No content published yet</p>
                  )}
                </div>

                <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-300">
                    <strong>💡 Tip:</strong> Consistent content publishing helps improve your visibility in the directory and builds trust with potential customers.
                  </p>
                </div>
              </AnalyticsSection>
            )}

            {/* Directory Listing Analytics */}
            {activeTab === 'directory' && directoryStats && (
              <AnalyticsSection
                title="Directory Listing Analytics"
                description="Performance metrics for your business listing in AI Business Finder"
                icon={Building}
                color="from-emerald-600/80 to-teal-600/80"
              >
                {directoryStats.hasListing ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <StatCard
                        title="Listing Views"
                        value={directoryStats.views.toLocaleString()}
                        icon={Eye}
                        color="from-blue-500 to-cyan-600"
                      />
                      <StatCard
                        title="Likes"
                        value={directoryStats.likes}
                        icon={Heart}
                        color="from-pink-500 to-rose-600"
                      />
                      <StatCard
                        title="Inquiries"
                        value={directoryStats.inquiries}
                        icon={MessageSquare}
                        color="from-amber-500 to-orange-600"
                      />
                      <StatCard
                        title="Rating"
                        value={directoryStats.rating.toFixed(1)}
                        icon={TrendingUp}
                        color="from-emerald-500 to-teal-600"
                      />
                    </div>

                    {/* Status */}
                    <div className="bg-[#0d1525] rounded-xl p-6 border border-white/10">
                      <h3 className="font-semibold text-white mb-4">Listing Status</h3>
                      <div className="flex items-center gap-4">
                        <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                          directoryStats.isPublished 
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        }`}>
                          {directoryStats.isPublished ? 'Published to Directory' : 'Draft - Not Published'}
                        </div>
                        <span className="text-gray-400 text-sm">
                          {directoryStats.reviewCount} reviews
                        </span>
                      </div>
                    </div>

                    {!directoryStats.isPublished && (
                      <div className="mt-6 p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                        <p className="text-sm text-amber-300">
                          <strong>⚠️ Action Required:</strong> Your listing is not published. Go to My Business Listing to publish it to the AI Business Finder directory.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Building className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No Business Listing</h3>
                    <p className="text-gray-400 mb-4">Create your business listing to appear in the AI Business Finder directory</p>
                    <Button className="bg-gradient-to-r from-emerald-500 to-teal-500">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Create Business Listing
                    </Button>
                  </div>
                )}
              </AnalyticsSection>
            )}
          </>
        )}
        </div>
      </div>
    </DashboardLayout>
  );
}
